import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, X, Volume2, ExternalLink } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";

const fmt = (t) => {
    if (!t || Number.isNaN(t)) return "0:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
};

const SPEEDS = [1, 1.25, 1.5, 2];

export default function AudioPlayer() {
    const { audioRef, current, playing, setPlaying, close } = usePlayer();
    const [time, setTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.9);
    const [speedIdx, setSpeedIdx] = useState(0);

    useEffect(() => {
        setTime(0);
        setDuration(0);
    }, [current?.id]);

    useEffect(() => {
        if (audioRef.current) audioRef.current.volume = volume;
    }, [volume, audioRef]);

    useEffect(() => {
        if (audioRef.current) audioRef.current.playbackRate = SPEEDS[speedIdx];
    }, [speedIdx, audioRef]);

    const toggle = () => {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) a.play();
        else a.pause();
    };

    const seek = (e) => {
        const a = audioRef.current;
        if (!a || !duration) return;
        const v = Number(e.target.value);
        a.currentTime = v;
        setTime(v);
    };

    return (
        <AnimatePresence>
            {current && (
                <motion.div
                    key="player"
                    data-testid="audio-player-bar"
                    initial={{ y: 120 }}
                    animate={{ y: 0 }}
                    exit={{ y: 120 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="fixed bottom-0 inset-x-0 z-50 bg-paper border-t-2 border-ink"
                >
                    <audio
                        ref={audioRef}
                        src={current.audio_url}
                        autoPlay
                        data-testid="audio-element"
                        onPlay={() => setPlaying(true)}
                        onPause={() => setPlaying(false)}
                        onEnded={() => setPlaying(false)}
                        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
                        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                    />
                    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center gap-3 sm:gap-5">
                        <img
                            src={current.poster_art}
                            alt={current.title}
                            className="w-12 h-12 sm:w-14 sm:h-14 object-cover object-top border-2 border-ink hard-shadow-sm shrink-0"
                        />
                        <div className="min-w-0 w-36 sm:w-52 shrink-0">
                            <p className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-pink">
                                EP {current.number}
                            </p>
                            <p
                                data-testid="player-episode-title"
                                className="font-sub font-bold text-sm sm:text-base text-ink truncate"
                            >
                                {current.title}
                            </p>
                        </div>

                        <button
                            data-testid="audio-play-pause-btn"
                            onClick={toggle}
                            aria-label={playing ? "Pause" : "Play"}
                            className="w-11 h-11 shrink-0 flex items-center justify-center bg-pink text-cream border-2 border-ink hard-shadow-sm transition-transform duration-150 hover:-translate-y-0.5"
                        >
                            {playing ? (
                                <Pause className="w-5 h-5" fill="currentColor" />
                            ) : (
                                <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                            )}
                        </button>

                        <div className="hidden sm:flex items-center gap-3 flex-1 min-w-0">
                            <span className="font-mono-brand text-xs text-ink w-10 text-right shrink-0">
                                {fmt(time)}
                            </span>
                            <input
                                type="range"
                                data-testid="audio-scrub-slider"
                                className="player-range flex-1"
                                min={0}
                                max={duration || 0}
                                step={0.1}
                                value={time}
                                onChange={seek}
                                aria-label="Seek"
                            />
                            <span className="font-mono-brand text-xs text-ink w-10 shrink-0">
                                {fmt(duration)}
                            </span>
                        </div>

                        <div className="hidden lg:flex items-center gap-2 shrink-0">
                            <Volume2 className="w-4 h-4 text-ink" />
                            <input
                                type="range"
                                data-testid="audio-volume-slider"
                                className="player-range w-20"
                                min={0}
                                max={1}
                                step={0.05}
                                value={volume}
                                onChange={(e) => setVolume(Number(e.target.value))}
                                aria-label="Volume"
                            />
                        </div>

                        <button
                            data-testid="audio-speed-toggle"
                            onClick={() => setSpeedIdx((i) => (i + 1) % SPEEDS.length)}
                            className="hidden sm:block shrink-0 font-mono-brand text-xs font-semibold border-2 border-ink px-2.5 py-1.5 bg-cream text-ink transition-colors duration-150 hover:bg-mustard/40"
                        >
                            {SPEEDS[speedIdx]}x
                        </button>

                        <a
                            href={current.spotify_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="player-spotify-link"
                            aria-label="Open on Spotify"
                            className="hidden md:flex shrink-0 items-center gap-1.5 font-mono-brand text-xs uppercase tracking-wider text-ink hover:text-pink transition-colors duration-150"
                        >
                            Spotify <ExternalLink className="w-3.5 h-3.5" />
                        </a>

                        <button
                            data-testid="audio-close-btn"
                            onClick={close}
                            aria-label="Close player"
                            className="shrink-0 p-2 border-2 border-ink bg-cream text-ink transition-colors duration-150 hover:bg-pink hover:text-cream"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
